import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useCMS } from '../../context/CMSContext';
import { toast, Toaster } from 'sonner';
import { 
  Plus, Edit2, Trash2, X, Save, Star, Check, ChevronDown, ChevronUp,
  Package, Layers, Grid3X3, Rocket, TrendingUp, Crown, Globe, Smartphone,
  Users, Shield, FileText, Brain, BarChart, AlertTriangle, Wallet,
  ClipboardList, Heart, Settings, Bell, GraduationCap, Home, Briefcase,
  Lock, Headphones, Eye, Sparkles
} from 'lucide-react';

// Icon mapping for selection
const iconOptions = [
  { name: 'Globe', icon: Globe },
  { name: 'Smartphone', icon: Smartphone },
  { name: 'Users', icon: Users },
  { name: 'Shield', icon: Shield },
  { name: 'FileText', icon: FileText },
  { name: 'Brain', icon: Brain },
  { name: 'BarChart', icon: BarChart },
  { name: 'AlertTriangle', icon: AlertTriangle },
  { name: 'Wallet', icon: Wallet },
  { name: 'ClipboardList', icon: ClipboardList },
  { name: 'Heart', icon: Heart },
  { name: 'Settings', icon: Settings },
  { name: 'Bell', icon: Bell },
  { name: 'GraduationCap', icon: GraduationCap },
  { name: 'Home', icon: Home },
  { name: 'Briefcase', icon: Briefcase },
  { name: 'Rocket', icon: Rocket },
  { name: 'TrendingUp', icon: TrendingUp },
  { name: 'Crown', icon: Crown },
  { name: 'Package', icon: Package },
  { name: 'Layers', icon: Layers },
  { name: 'Lock', icon: Lock },
  { name: 'Headphones', icon: Headphones },
];

const colorOptions = [
  'tech-blue', 'care-teal', 'primary-navy', 'purple-600', 'red-500', 
  'emerald-500', 'amber-500', 'pink-500', 'indigo-500', 'cyan-500'
];

const getIconComponent = (iconName) => {
  const found = iconOptions.find(i => i.name === iconName);
  return found ? found.icon : Globe;
};

const AdminFeaturesPricingPage = () => {
  const { 
    getFeaturePackages, createFeaturePackage, updateFeaturePackage, deleteFeaturePackage,
    getFeatureSections, createFeatureSection, updateFeatureSection, deleteFeatureSection,
    getFeatureCategories, createFeatureCategory, updateFeatureCategory, deleteFeatureCategory,
    getFPPageSettings, updateFPPageSettings
  } = useCMS();
  
  const [activeTab, setActiveTab] = useState('settings');
  const [packages, setPackages] = useState([]);
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pageSettings, setPageSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Form states
  const [packageForm, setPackageForm] = useState({
    name: '', slug: '', icon: 'Rocket', color: 'tech-blue',
    gradient: 'from-tech-blue to-blue-500', staff_range: '',
    build_price: '', hosting_price: '', description: '',
    features: [], is_popular: false, order: 0
  });
  
  const [sectionForm, setSectionForm] = useState({
    title: '', slug: '', icon: 'Globe', color: 'tech-blue',
    features: [], all_plans: false, order: 0
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '', slug: '', icon: 'Globe', color: 'tech-blue',
    features: [], order: 0
  });

  const [settingsForm, setSettingsForm] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_badge: '',
    meta_title: '',
    meta_description: '',
    stats: [],
    trust_badges: [],
    explorer_title: '',
    explorer_subtitle: '',
    pricing_title: '',
    pricing_subtitle: '',
    pricing_badge: '',
    comparison_title: '',
    comparison_subtitle: '',
    show_pricing_packages: true
  });

  const [newFeature, setNewFeature] = useState('');
  const [newFeatureItem, setNewFeatureItem] = useState({ name: '', starter: '', growth: '', enterprise: '' });
  const [newStat, setNewStat] = useState({ value: '', suffix: '', label: '' });
  const [newBadge, setNewBadge] = useState({ icon: 'Shield', text: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pkgs, secs, cats, settings] = await Promise.all([
        getFeaturePackages(false),
        getFeatureSections(false),
        getFeatureCategories(false),
        getFPPageSettings()
      ]);
      setPackages(pkgs || []);
      setSections(secs || []);
      setCategories(cats || []);
      if (settings) {
        setPageSettings(settings);
        setSettingsForm({
          hero_title: settings.hero_title || '',
          hero_subtitle: settings.hero_subtitle || '',
          hero_badge: settings.hero_badge || '',
          meta_title: settings.meta_title || '',
          meta_description: settings.meta_description || '',
          stats: settings.stats || [],
          trust_badges: settings.trust_badges || [],
          explorer_title: settings.explorer_title || '',
          explorer_subtitle: settings.explorer_subtitle || '',
          pricing_title: settings.pricing_title || '',
          pricing_subtitle: settings.pricing_subtitle || '',
          pricing_badge: settings.pricing_badge || '',
          comparison_title: settings.comparison_title || '',
          comparison_subtitle: settings.comparison_subtitle || ''
        });
      }
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Page Settings handlers
  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await updateFPPageSettings(settingsForm);
      toast.success('Page settings saved');
      loadData();
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const addStat = () => {
    if (newStat.value && newStat.label) {
      setSettingsForm(prev => ({
        ...prev,
        stats: [...prev.stats, { ...newStat }]
      }));
      setNewStat({ value: '', suffix: '', label: '' });
    }
  };

  const removeStat = (index) => {
    setSettingsForm(prev => ({
      ...prev,
      stats: prev.stats.filter((_, i) => i !== index)
    }));
  };

  const addBadge = () => {
    if (newBadge.text) {
      setSettingsForm(prev => ({
        ...prev,
        trust_badges: [...prev.trust_badges, { ...newBadge }]
      }));
      setNewBadge({ icon: 'Shield', text: '' });
    }
  };

  const removeBadge = (index) => {
    setSettingsForm(prev => ({
      ...prev,
      trust_badges: prev.trust_badges.filter((_, i) => i !== index)
    }));
  };

  // Package handlers
  const handlePackageSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateFeaturePackage(editingId, packageForm);
        toast.success('Package updated');
      } else {
        await createFeaturePackage(packageForm);
        toast.success('Package created');
      }
      resetForm();
      loadData();
    } catch (e) {
      toast.error('Failed to save package');
    }
  };

  const handlePackageDelete = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await deleteFeaturePackage(id);
      toast.success('Package deleted');
      loadData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handlePackageEdit = (pkg) => {
    setPackageForm({
      name: pkg.name || '',
      slug: pkg.slug || '',
      icon: pkg.icon || 'Rocket',
      color: pkg.color || 'tech-blue',
      gradient: pkg.gradient || 'from-tech-blue to-blue-500',
      staff_range: pkg.staff_range || '',
      build_price: pkg.build_price || '',
      hosting_price: pkg.hosting_price || '',
      description: pkg.description || '',
      features: pkg.features || [],
      is_popular: pkg.is_popular || false,
      order: pkg.order || 0
    });
    setEditingId(pkg.id);
    setShowForm(true);
  };

  // Section handlers
  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateFeatureSection(editingId, sectionForm);
        toast.success('Section updated');
      } else {
        await createFeatureSection(sectionForm);
        toast.success('Section created');
      }
      resetForm();
      loadData();
    } catch (e) {
      toast.error('Failed to save section');
    }
  };

  const handleSectionDelete = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await deleteFeatureSection(id);
      toast.success('Section deleted');
      loadData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleSectionEdit = (sec) => {
    setSectionForm({
      title: sec.title || '',
      slug: sec.slug || '',
      icon: sec.icon || 'Globe',
      color: sec.color || 'tech-blue',
      features: sec.features || [],
      all_plans: sec.all_plans || false,
      order: sec.order || 0
    });
    setEditingId(sec.id);
    setShowForm(true);
  };

  // Category handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateFeatureCategory(editingId, categoryForm);
        toast.success('Category updated');
      } else {
        await createFeatureCategory(categoryForm);
        toast.success('Category created');
      }
      resetForm();
      loadData();
    } catch (e) {
      toast.error('Failed to save category');
    }
  };

  const handleCategoryDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await deleteFeatureCategory(id);
      toast.success('Category deleted');
      loadData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleCategoryEdit = (cat) => {
    setCategoryForm({
      name: cat.name || '',
      slug: cat.slug || '',
      icon: cat.icon || 'Globe',
      color: cat.color || 'tech-blue',
      features: cat.features || [],
      order: cat.order || 0
    });
    setEditingId(cat.id);
    setShowForm(true);
  };

  // Toggle active status
  const handleToggleActive = async (item, type) => {
    try {
      if (type === 'package') {
        await updateFeaturePackage(item.id, { is_active: !item.is_active });
      } else if (type === 'section') {
        await updateFeatureSection(item.id, { is_active: !item.is_active });
      } else {
        await updateFeatureCategory(item.id, { is_active: !item.is_active });
      }
      toast.success(`${item.is_active ? 'Hidden' : 'Shown'}`);
      loadData();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  // Feature list helpers
  const addPackageFeature = () => {
    if (newFeature.trim()) {
      setPackageForm(prev => ({ ...prev, features: [...prev.features, newFeature.trim()] }));
      setNewFeature('');
    }
  };

  const removePackageFeature = (index) => {
    setPackageForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  const addFeatureItem = (formType) => {
    if (newFeatureItem.name.trim()) {
      const item = {
        name: newFeatureItem.name,
        starter: parseFeatureValue(newFeatureItem.starter),
        growth: parseFeatureValue(newFeatureItem.growth),
        enterprise: parseFeatureValue(newFeatureItem.enterprise)
      };
      if (formType === 'section') {
        setSectionForm(prev => ({ ...prev, features: [...prev.features, item] }));
      } else {
        setCategoryForm(prev => ({ ...prev, features: [...prev.features, item] }));
      }
      setNewFeatureItem({ name: '', starter: '', growth: '', enterprise: '' });
    }
  };

  const parseFeatureValue = (value) => {
    if (value === 'true' || value === '✓') return true;
    if (value === 'false' || value === '✗' || value === '') return false;
    return value;
  };

  const removeFeatureItem = (index, formType) => {
    if (formType === 'section') {
      setSectionForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
    } else {
      setCategoryForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
    }
  };

  const resetForm = () => {
    setPackageForm({
      name: '', slug: '', icon: 'Rocket', color: 'tech-blue',
      gradient: 'from-tech-blue to-blue-500', staff_range: '',
      build_price: '', hosting_price: '', description: '',
      features: [], is_popular: false, order: 0
    });
    setSectionForm({
      title: '', slug: '', icon: 'Globe', color: 'tech-blue',
      features: [], all_plans: false, order: 0
    });
    setCategoryForm({
      name: '', slug: '', icon: 'Globe', color: 'tech-blue',
      features: [], order: 0
    });
    setNewFeature('');
    setNewFeatureItem({ name: '', starter: '', growth: '', enterprise: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const renderFeatureValue = (val) => {
    if (val === true) return <Check className="text-care-teal" size={14} />;
    if (val === false) return <X className="text-gray-300" size={14} />;
    return <span className="text-xs">{val}</span>;
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="admin-features-pricing">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Features & Pricing CMS</h1>
          <p className="text-text-secondary mt-1">Manage all content for the Features & Pricing page</p>
        </div>
        {activeTab !== 'settings' && (
          <Button onClick={() => setShowForm(true)} className="bg-tech-blue hover:bg-tech-blue/90">
            <Plus size={18} className="mr-2" />
            Add {activeTab === 'packages' ? 'Package' : activeTab === 'sections' ? 'Section' : 'Category'}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border-line pb-4 overflow-x-auto">
        {[
          { id: 'settings', label: 'Page Settings', icon: Settings, count: null },
          { id: 'packages', label: 'Pricing Packages', icon: Package, count: packages.length },
          { id: 'sections', label: 'Feature Sections', icon: Layers, count: sections.length },
          { id: 'categories', label: 'Feature Categories', icon: Grid3X3, count: categories.length }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-tech-blue text-white' 
                  : 'bg-surface text-text-secondary hover:bg-gray-100'
              }`}
            >
              <Icon size={18} />
              {tab.label}
              {tab.count !== null && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Page Settings Tab */}
      {activeTab === 'settings' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Hero Section Settings */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="text-tech-blue" size={20} />
                Hero Section
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Badge Text</label>
                <input
                  value={settingsForm.hero_badge}
                  onChange={(e) => setSettingsForm({ ...settingsForm, hero_badge: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  placeholder="The GetCare 360 Advantage"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Hero Title</label>
                <input
                  value={settingsForm.hero_title}
                  onChange={(e) => setSettingsForm({ ...settingsForm, hero_title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  placeholder="Everything You Need to Run a Modern NDIS Business"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Hero Subtitle</label>
                <textarea
                  rows={2}
                  value={settingsForm.hero_subtitle}
                  onChange={(e) => setSettingsForm({ ...settingsForm, hero_subtitle: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                  placeholder="One platform. Complete ownership. No per-user fees."
                />
              </div>
            </CardContent>
          </Card>

          {/* Section Visibility Settings */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="text-purple-500" size={20} />
                Section Visibility
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-page-bg rounded-lg">
                <div>
                  <h4 className="font-medium text-text-primary">Pricing Packages</h4>
                  <p className="text-sm text-text-secondary">Show pricing tiers (Starter, Growth, Enterprise) on public page</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.show_pricing_packages !== false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, show_pricing_packages: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-tech-blue"></div>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* SEO Settings */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="text-care-teal" size={20} />
                SEO & Meta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Meta Title</label>
                <input
                  value={settingsForm.meta_title}
                  onChange={(e) => setSettingsForm({ ...settingsForm, meta_title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  placeholder="Features & Pricing | GetCare 360"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Meta Description</label>
                <textarea
                  rows={3}
                  value={settingsForm.meta_description}
                  onChange={(e) => setSettingsForm({ ...settingsForm, meta_description: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                  placeholder="Explore GetCare 360 features and transparent pricing..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Stats Section */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart className="text-purple-500" size={20} />
                Hero Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <input
                  value={newStat.value}
                  onChange={(e) => setNewStat({ ...newStat, value: e.target.value })}
                  className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                  placeholder="Value (e.g., 300)"
                />
                <input
                  value={newStat.suffix}
                  onChange={(e) => setNewStat({ ...newStat, suffix: e.target.value })}
                  className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                  placeholder="Suffix (e.g., +)"
                />
                <div className="flex gap-1">
                  <input
                    value={newStat.label}
                    onChange={(e) => setNewStat({ ...newStat, label: e.target.value })}
                    className="flex-1 px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                    placeholder="Label"
                  />
                  <Button type="button" onClick={addStat} variant="outline" size="sm"><Plus size={16} /></Button>
                </div>
              </div>
              <div className="space-y-2">
                {settingsForm.stats.map((stat, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-page-bg rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl font-bold text-tech-blue">{stat.value}{stat.suffix}</span>
                      <span className="text-text-secondary">{stat.label}</span>
                    </div>
                    <button onClick={() => removeStat(i)} className="text-red-500"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Trust Badges */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="text-care-teal" size={20} />
                Trust Badges
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <select
                  value={newBadge.icon}
                  onChange={(e) => setNewBadge({ ...newBadge, icon: e.target.value })}
                  className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                >
                  {iconOptions.map(opt => <option key={opt.name} value={opt.name}>{opt.name}</option>)}
                </select>
                <div className="col-span-2 flex gap-1">
                  <input
                    value={newBadge.text}
                    onChange={(e) => setNewBadge({ ...newBadge, text: e.target.value })}
                    className="flex-1 px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                    placeholder="Badge text"
                  />
                  <Button type="button" onClick={addBadge} variant="outline" size="sm"><Plus size={16} /></Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {settingsForm.trust_badges.map((badge, i) => {
                  const Icon = getIconComponent(badge.icon);
                  return (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 bg-page-bg rounded-full group">
                      <Icon size={14} className="text-care-teal" />
                      <span className="text-sm text-text-secondary">{badge.text}</span>
                      <button onClick={() => removeBadge(i)} className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Feature Explorer Section */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="text-tech-blue" size={20} />
                Feature Explorer Section
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Section Title</label>
                <input
                  value={settingsForm.explorer_title}
                  onChange={(e) => setSettingsForm({ ...settingsForm, explorer_title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  placeholder="Explore Features by Category"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Section Subtitle</label>
                <input
                  value={settingsForm.explorer_subtitle}
                  onChange={(e) => setSettingsForm({ ...settingsForm, explorer_subtitle: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  placeholder="Click on categories to explore features..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Pricing Section */}
          <Card className="border-border-line">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="text-heritage-orange" size={20} />
                Pricing Section
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">Badge Text</label>
                <input
                  value={settingsForm.pricing_badge}
                  onChange={(e) => setSettingsForm({ ...settingsForm, pricing_badge: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  placeholder="One-Time Investment Model"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Section Title</label>
                <input
                  value={settingsForm.pricing_title}
                  onChange={(e) => setSettingsForm({ ...settingsForm, pricing_title: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  placeholder="Choose Your Investment Package"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">Section Subtitle</label>
                <input
                  value={settingsForm.pricing_subtitle}
                  onChange={(e) => setSettingsForm({ ...settingsForm, pricing_subtitle: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                  placeholder="Build once, own forever..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Comparison Section */}
          <Card className="border-border-line lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="text-primary-navy" size={20} />
                Feature Comparison Section
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1">Section Title</label>
                  <input
                    value={settingsForm.comparison_title}
                    onChange={(e) => setSettingsForm({ ...settingsForm, comparison_title: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    placeholder="Complete Feature Comparison"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1">Section Subtitle</label>
                  <input
                    value={settingsForm.comparison_subtitle}
                    onChange={(e) => setSettingsForm({ ...settingsForm, comparison_subtitle: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                    placeholder="See exactly what's included in each package"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="lg:col-span-2 flex justify-end">
            <Button 
              onClick={handleSaveSettings} 
              disabled={savingSettings}
              className="bg-care-teal hover:bg-care-teal/90 px-8"
            >
              <Save size={18} className="mr-2" />
              {savingSettings ? 'Saving...' : 'Save All Settings'}
            </Button>
          </div>
        </div>
      )}

      {/* Packages Tab */}
      {activeTab === 'packages' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.sort((a, b) => a.order - b.order).map(pkg => {
            const Icon = getIconComponent(pkg.icon);
            return (
              <Card key={pkg.id} className={`border-border-line relative ${!pkg.is_active ? 'opacity-50' : ''} ${pkg.is_popular ? 'border-tech-blue border-2' : ''}`}>
                {pkg.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-tech-blue text-white text-xs px-3 py-1 rounded-full flex items-center">
                      <Star size={12} className="mr-1 fill-white" /> Popular
                    </span>
                  </div>
                )}
                <CardContent className="p-6 pt-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-text-secondary">Order: {pkg.order}</span>
                    <button
                      onClick={() => handleToggleActive(pkg, 'package')}
                      className={`text-xs px-2 py-1 rounded ${pkg.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {pkg.is_active ? 'Active' : 'Hidden'}
                    </button>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg bg-${pkg.color}/10 flex items-center justify-center`}>
                      <Icon className={`text-${pkg.color}`} size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-text-primary">{pkg.name}</h3>
                      <p className="text-xs text-text-secondary">{pkg.staff_range}</p>
                    </div>
                  </div>
                  <div className="mb-3">
                    <p className="text-sm text-text-secondary">Build: <span className="font-semibold text-tech-blue">{pkg.build_price}</span></p>
                    <p className="text-sm text-text-secondary">Hosting: <span className="font-semibold">{pkg.hosting_price}/mo</span></p>
                  </div>
                  <ul className="space-y-1 mb-4">
                    {pkg.features?.slice(0, 4).map((f, i) => (
                      <li key={i} className="text-xs text-text-secondary flex items-center">
                        <Check size={12} className="text-care-teal mr-1" /> {f}
                      </li>
                    ))}
                    {pkg.features?.length > 4 && (
                      <li className="text-xs text-tech-blue">+{pkg.features.length - 4} more</li>
                    )}
                  </ul>
                  <div className="flex justify-end gap-2 pt-3 border-t border-border-line">
                    <button onClick={() => handlePackageEdit(pkg)} className="p-2 text-text-secondary hover:text-tech-blue">
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => handlePackageDelete(pkg.id)} className="p-2 text-text-secondary hover:text-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {packages.length === 0 && (
            <div className="col-span-3 text-center py-12 text-text-secondary">
              No packages yet. Click "Add Package" to create one.
            </div>
          )}
        </div>
      )}

      {/* Sections Tab */}
      {activeTab === 'sections' && (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary mb-4">{sections.length} sections loaded from CMS</p>
          {sections.sort((a, b) => a.order - b.order).map(sec => {
            const Icon = getIconComponent(sec.icon);
            const isExpanded = expandedSection === sec.id;
            return (
              <Card key={sec.id} className={`border-border-line ${!sec.is_active ? 'opacity-50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-${sec.color}/10 flex items-center justify-center`}>
                        <Icon className={`text-${sec.color}`} size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-text-primary">{sec.title}</h3>
                        <p className="text-xs text-text-secondary">{sec.features?.length || 0} features • Order: {sec.order}</p>
                      </div>
                      {sec.all_plans && (
                        <span className="text-xs bg-care-teal/10 text-care-teal px-2 py-1 rounded">All Plans</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(sec, 'section')}
                        className={`text-xs px-2 py-1 rounded ${sec.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}
                      >
                        {sec.is_active ? 'Active' : 'Hidden'}
                      </button>
                      <button onClick={() => setExpandedSection(isExpanded ? null : sec.id)} className="p-2 text-text-secondary hover:text-tech-blue">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      <button onClick={() => handleSectionEdit(sec)} className="p-2 text-text-secondary hover:text-tech-blue">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleSectionDelete(sec.id)} className="p-2 text-text-secondary hover:text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {isExpanded && sec.features?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border-line overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-text-secondary">
                            <th className="pb-2">Feature</th>
                            <th className="pb-2 text-center">Starter</th>
                            <th className="pb-2 text-center">Growth</th>
                            <th className="pb-2 text-center">Enterprise</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sec.features.map((f, i) => (
                            <tr key={i} className="border-t border-border-line">
                              <td className="py-2 text-text-primary">{f.name}</td>
                              <td className="py-2 text-center">{renderFeatureValue(f.starter)}</td>
                              <td className="py-2 text-center">{renderFeatureValue(f.growth)}</td>
                              <td className="py-2 text-center">{renderFeatureValue(f.enterprise)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {sections.length === 0 && (
            <div className="text-center py-12 text-text-secondary">
              No feature sections yet. Click "Add Section" to create one.
            </div>
          )}
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-4">
          {categories.sort((a, b) => a.order - b.order).map(cat => {
            const Icon = getIconComponent(cat.icon);
            const isExpanded = expandedSection === cat.id;
            return (
              <Card key={cat.id} className={`border-border-line ${!cat.is_active ? 'opacity-50' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-${cat.color}/10 flex items-center justify-center`}>
                        <Icon className={`text-${cat.color}`} size={20} />
                      </div>
                      <div>
                        <h3 className="font-bold text-text-primary">{cat.name}</h3>
                        <p className="text-xs text-text-secondary">{cat.features?.length || 0} features • Order: {cat.order}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(cat, 'category')}
                        className={`text-xs px-2 py-1 rounded ${cat.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}
                      >
                        {cat.is_active ? 'Active' : 'Hidden'}
                      </button>
                      <button onClick={() => setExpandedSection(isExpanded ? null : cat.id)} className="p-2 text-text-secondary hover:text-tech-blue">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      <button onClick={() => handleCategoryEdit(cat)} className="p-2 text-text-secondary hover:text-tech-blue">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleCategoryDelete(cat.id)} className="p-2 text-text-secondary hover:text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {isExpanded && cat.features?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-border-line overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-text-secondary">
                            <th className="pb-2">Feature</th>
                            <th className="pb-2 text-center">Starter</th>
                            <th className="pb-2 text-center">Growth</th>
                            <th className="pb-2 text-center">Enterprise</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cat.features.map((f, i) => (
                            <tr key={i} className="border-t border-border-line">
                              <td className="py-2 text-text-primary">{f.name}</td>
                              <td className="py-2 text-center">{renderFeatureValue(f.starter)}</td>
                              <td className="py-2 text-center">{renderFeatureValue(f.growth)}</td>
                              <td className="py-2 text-center">{renderFeatureValue(f.enterprise)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {categories.length === 0 && (
            <div className="text-center py-12 text-text-secondary">
              No feature categories yet. Click "Add Category" to create one.
            </div>
          )}
        </div>
      )}

      {/* Form Modal for Packages/Sections/Categories */}
      {showForm && activeTab !== 'settings' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl border-border-line my-8 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between pb-3 sticky top-0 bg-white z-10">
              <CardTitle>
                {editingId ? 'Edit' : 'Add'} {activeTab === 'packages' ? 'Package' : activeTab === 'sections' ? 'Section' : 'Category'}
              </CardTitle>
              <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                <X size={20} />
              </button>
            </CardHeader>
            <CardContent>
              {/* Package Form */}
              {activeTab === 'packages' && (
                <form onSubmit={handlePackageSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Package Name *</label>
                      <input
                        required
                        value={packageForm.name}
                        onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., STARTER"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Slug *</label>
                      <input
                        required
                        value={packageForm.slug}
                        onChange={(e) => setPackageForm({ ...packageForm, slug: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., starter"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Build Price</label>
                      <input
                        value={packageForm.build_price}
                        onChange={(e) => setPackageForm({ ...packageForm, build_price: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., $18,500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Hosting Price</label>
                      <input
                        value={packageForm.hosting_price}
                        onChange={(e) => setPackageForm({ ...packageForm, hosting_price: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., $390"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Staff Range</label>
                      <input
                        value={packageForm.staff_range}
                        onChange={(e) => setPackageForm({ ...packageForm, staff_range: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., 5-15 Staff"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Icon</label>
                      <select
                        value={packageForm.icon}
                        onChange={(e) => setPackageForm({ ...packageForm, icon: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      >
                        {iconOptions.map(opt => <option key={opt.name} value={opt.name}>{opt.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Order</label>
                      <input
                        type="number"
                        value={packageForm.order}
                        onChange={(e) => setPackageForm({ ...packageForm, order: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Description</label>
                    <textarea
                      rows={2}
                      value={packageForm.description}
                      onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={packageForm.is_popular}
                      onChange={(e) => setPackageForm({ ...packageForm, is_popular: e.target.checked })}
                      className="mr-2"
                    />
                    <label className="text-sm text-text-secondary">Mark as Popular</label>
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Features</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPackageFeature())}
                        className="flex-1 px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="Add feature..."
                      />
                      <Button type="button" onClick={addPackageFeature} variant="outline"><Plus size={18} /></Button>
                    </div>
                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                      {packageForm.features.map((f, i) => (
                        <li key={i} className="flex items-center justify-between p-2 bg-page-bg rounded text-sm">
                          <span className="flex items-center"><Check size={14} className="text-care-teal mr-2" />{f}</span>
                          <button type="button" onClick={() => removePackageFeature(i)} className="text-red-500"><X size={14} /></button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                    <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                      <Save size={18} className="mr-2" />{editingId ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Section Form */}
              {activeTab === 'sections' && (
                <form onSubmit={handleSectionSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Title *</label>
                      <input
                        required
                        value={sectionForm.title}
                        onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., Platform & Ownership"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Slug *</label>
                      <input
                        required
                        value={sectionForm.slug}
                        onChange={(e) => setSectionForm({ ...sectionForm, slug: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., platform"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Icon</label>
                      <select
                        value={sectionForm.icon}
                        onChange={(e) => setSectionForm({ ...sectionForm, icon: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      >
                        {iconOptions.map(opt => <option key={opt.name} value={opt.name}>{opt.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Color</label>
                      <select
                        value={sectionForm.color}
                        onChange={(e) => setSectionForm({ ...sectionForm, color: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      >
                        {colorOptions.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Order</label>
                      <input
                        type="number"
                        value={sectionForm.order}
                        onChange={(e) => setSectionForm({ ...sectionForm, order: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={sectionForm.all_plans}
                      onChange={(e) => setSectionForm({ ...sectionForm, all_plans: e.target.checked })}
                      className="mr-2"
                    />
                    <label className="text-sm text-text-secondary">All Plans (show badge)</label>
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Features</label>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      <input
                        value={newFeatureItem.name}
                        onChange={(e) => setNewFeatureItem({ ...newFeatureItem, name: e.target.value })}
                        className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                        placeholder="Feature name"
                      />
                      <input
                        value={newFeatureItem.starter}
                        onChange={(e) => setNewFeatureItem({ ...newFeatureItem, starter: e.target.value })}
                        className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                        placeholder="Starter"
                      />
                      <input
                        value={newFeatureItem.growth}
                        onChange={(e) => setNewFeatureItem({ ...newFeatureItem, growth: e.target.value })}
                        className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                        placeholder="Growth"
                      />
                      <div className="flex gap-1">
                        <input
                          value={newFeatureItem.enterprise}
                          onChange={(e) => setNewFeatureItem({ ...newFeatureItem, enterprise: e.target.value })}
                          className="flex-1 px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                          placeholder="Enterprise"
                        />
                        <Button type="button" onClick={() => addFeatureItem('section')} variant="outline" size="sm"><Plus size={16} /></Button>
                      </div>
                    </div>
                    <p className="text-xs text-text-secondary mb-2">Use ✓ or "true" for checkmark, ✗ or "false" for X</p>
                    {sectionForm.features.length > 0 && (
                      <div className="border border-border-line rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-page-bg sticky top-0">
                            <tr>
                              <th className="p-2 text-left text-text-secondary">Feature</th>
                              <th className="p-2 text-center text-text-secondary">S</th>
                              <th className="p-2 text-center text-text-secondary">G</th>
                              <th className="p-2 text-center text-text-secondary">E</th>
                              <th className="p-2 w-8"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sectionForm.features.map((f, i) => (
                              <tr key={i} className="border-t border-border-line">
                                <td className="p-2 text-text-primary">{f.name}</td>
                                <td className="p-2 text-center">{renderFeatureValue(f.starter)}</td>
                                <td className="p-2 text-center">{renderFeatureValue(f.growth)}</td>
                                <td className="p-2 text-center">{renderFeatureValue(f.enterprise)}</td>
                                <td className="p-2">
                                  <button type="button" onClick={() => removeFeatureItem(i, 'section')} className="text-red-500"><X size={14} /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                    <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                      <Save size={18} className="mr-2" />{editingId ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Category Form */}
              {activeTab === 'categories' && (
                <form onSubmit={handleCategorySubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Name *</label>
                      <input
                        required
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., Platform"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Slug *</label>
                      <input
                        required
                        value={categoryForm.slug}
                        onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., platform"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Icon</label>
                      <select
                        value={categoryForm.icon}
                        onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      >
                        {iconOptions.map(opt => <option key={opt.name} value={opt.name}>{opt.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Color</label>
                      <select
                        value={categoryForm.color}
                        onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      >
                        {colorOptions.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-text-secondary mb-2">Order</label>
                      <input
                        type="number"
                        value={categoryForm.order}
                        onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-text-secondary mb-2">Features</label>
                    <div className="grid grid-cols-4 gap-2 mb-2">
                      <input
                        value={newFeatureItem.name}
                        onChange={(e) => setNewFeatureItem({ ...newFeatureItem, name: e.target.value })}
                        className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                        placeholder="Feature name"
                      />
                      <input
                        value={newFeatureItem.starter}
                        onChange={(e) => setNewFeatureItem({ ...newFeatureItem, starter: e.target.value })}
                        className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                        placeholder="Starter"
                      />
                      <input
                        value={newFeatureItem.growth}
                        onChange={(e) => setNewFeatureItem({ ...newFeatureItem, growth: e.target.value })}
                        className="px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                        placeholder="Growth"
                      />
                      <div className="flex gap-1">
                        <input
                          value={newFeatureItem.enterprise}
                          onChange={(e) => setNewFeatureItem({ ...newFeatureItem, enterprise: e.target.value })}
                          className="flex-1 px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                          placeholder="Enterprise"
                        />
                        <Button type="button" onClick={() => addFeatureItem('category')} variant="outline" size="sm"><Plus size={16} /></Button>
                      </div>
                    </div>
                    {categoryForm.features.length > 0 && (
                      <div className="border border-border-line rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-page-bg sticky top-0">
                            <tr>
                              <th className="p-2 text-left text-text-secondary">Feature</th>
                              <th className="p-2 text-center text-text-secondary">S</th>
                              <th className="p-2 text-center text-text-secondary">G</th>
                              <th className="p-2 text-center text-text-secondary">E</th>
                              <th className="p-2 w-8"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {categoryForm.features.map((f, i) => (
                              <tr key={i} className="border-t border-border-line">
                                <td className="p-2 text-text-primary">{f.name}</td>
                                <td className="p-2 text-center">{renderFeatureValue(f.starter)}</td>
                                <td className="p-2 text-center">{renderFeatureValue(f.growth)}</td>
                                <td className="p-2 text-center">{renderFeatureValue(f.enterprise)}</td>
                                <td className="p-2">
                                  <button type="button" onClick={() => removeFeatureItem(i, 'category')} className="text-red-500"><X size={14} /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                    <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                      <Save size={18} className="mr-2" />{editingId ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminFeaturesPricingPage;
